// @core/stores/signature/SignatureStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { signatureApi } from '@core/api';
import type {
  CreatePartyRequest,
  CreateSignatureRequest,
  ListPartiesResponse,
  ListSignaturesResponse,
  PartyResponse,
  Signature,
  UpdatePartyRequest,
} from '@core/types/signature';
import type { PageInfo } from '@core/types/pagination';

class SignatureStore {
  // List state
  items: Signature[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Signature | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Signature>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Signature | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Signature | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create a signing party
   */
  async CreateParty(data: CreatePartyRequest): Promise<PartyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await signatureApi.CreateParty(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create a signature evidence record
   */
  async CreateSignature(data: CreateSignatureRequest): Promise<Signature> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await signatureApi.CreateSignature(data);
      const item = response.signature;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete a signing party
   */
  async DeleteParty(party_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await signatureApi.DeleteParty(party_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== party_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === party_id) {
          this.currentItem = null;
        }

        this.removeFromCache(party_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get party by ID
   */
  async GetParty(party_id: string): Promise<PartyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await signatureApi.GetParty(party_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get signature by ID
   */
  async GetSignature(signature_id: string): Promise<Signature> {
    // Check cache first
    const cached = this.cache.get(signature_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await signatureApi.GetSignature(signature_id);
      const item = response.signature;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List signing parties
   */
  async ListParties(params?: { limit?: string | number | boolean; cursor?: string | number | boolean; direction?: string | number | boolean; sort?: string | number | boolean; }): Promise<ListPartiesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await signatureApi.ListParties(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List parties of a target
   */
  async ListPartiesByTarget(target_type: string, target_id: string, params?: { limit?: string | number | boolean; cursor?: string | number | boolean; direction?: string | number | boolean; sort?: string | number | boolean; }): Promise<ListPartiesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await signatureApi.ListPartiesByTarget(target_type, target_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List signatures of a target
   */
  async ListSignaturesByTarget(target_type: string, target_id: string, params?: { limit?: string | number | boolean; cursor?: string | number | boolean; direction?: string | number | boolean; sort?: string | number | boolean; }): Promise<ListSignaturesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await signatureApi.ListSignaturesByTarget(target_type, target_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update a signing party
   */
  async UpdateParty(party_id: string, data: UpdatePartyRequest): Promise<PartyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await signatureApi.UpdateParty(party_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const signatureStore = new SignatureStore();
